import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Mail, Plus, Upload, Download, FileText, TrendingUp, Trash2, X, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_BADGE = {
  submitted: 'bg-amber-100 text-amber-700',
  draft: 'bg-slate-100 text-slate-600',
  in_progress: 'bg-blue-100 text-blue-700',
  sent: 'bg-emerald-100 text-emerald-700',
  reporting: 'bg-violet-100 text-violet-700',
};

const STATUS_LABEL = {
  submitted: 'Submitted', draft: 'Draft', in_progress: 'In Progress',
  sent: 'Sent', reporting: 'Reporting',
};

function NewCampaignModal({ open, onClose, onCreated, firmId }) {
  const [form, setForm] = useState({ name: '', objective: '', target_audience: '', content_requirements: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) { setForm({ name: '', objective: '', target_audience: '', content_requirements: '', notes: '' }); setError(''); }
  }, [open]);

  if (!open) return null;
  const submit = async () => {
    if (!form.name.trim()) { setError('Campaign name is required.'); return; }
    setSaving(true); setError('');
    try {
      const res = await axios.post(`${API}/email-marketing/${firmId}/campaigns`, form, { headers: authHeaders() });
      onCreated(res.data); onClose();
    } catch (e) { setError(e.response?.data?.detail || 'Failed to submit campaign.'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50" data-testid="new-email-campaign-modal">
      <div className="bg-white rounded-2xl max-w-xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>New Email Campaign</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        {error && <div className="mb-3 p-3 bg-rose-50 border border-rose-200 rounded-lg text-rose-700 text-sm">{error}</div>}
        <div className="space-y-3">
          {[
            ['name', 'Campaign Name *', 'e.g., March Newsletter - Personal Injury Clients', false],
            ['objective', 'Objective', 'e.g., Newsletter / Promotion / Re-engagement', false],
            ['target_audience', 'Target Audience', 'e.g., Existing personal-injury clients in London', false],
            ['content_requirements', 'Content Requirements', 'Describe content, tone, key messages, CTAs…', true],
            ['notes', 'Additional Notes', 'Anything else our team should know…', true],
          ].map(([k, label, ph, multi]) => (
            <div key={k}>
              <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
              {multi ? (
                <textarea value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} placeholder={ph}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 min-h-[80px]"
                  data-testid={`campaign-${k}-input`} />
              ) : (
                <input type="text" value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} placeholder={ph}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid={`campaign-${k}-input`} />
              )}
            </div>
          ))}
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50">Cancel</button>
          <button onClick={submit} disabled={saving} className="px-5 py-2 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50" data-testid="submit-campaign-btn">
            {saving ? 'Submitting…' : 'Submit Campaign'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function EmailMarketing({ user, selectedClient }) {
  const firmId = selectedClient?.firmId || selectedClient?.firm_id || user?.firm_id || user?.firm?.id;
  const canEdit = ['super_admin', 'internal_team'].includes(user?.role);

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null);

  const fetchData = useCallback(async () => {
    if (!firmId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/email-marketing/${firmId}/campaigns`, { headers: authHeaders() });
      setCampaigns(res.data || []);
    } catch (e) { setError(e.response?.data?.detail || 'Failed to load campaigns.'); }
    finally { setLoading(false); }
  }, [firmId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleListUpload = async (campaignId, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      await axios.post(`${API}/email-marketing/campaigns/${campaignId}/mailing-list`, fd, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      await fetchData(); alert('Mailing list uploaded.');
    } catch (e) { alert(e.response?.data?.detail || 'Upload failed.'); }
  };

  const handleReportUpload = async (campaignId, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await axios.post(`${API}/email-marketing/campaigns/${campaignId}/report`, fd, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      await fetchData(); alert(`Report uploaded. KPIs extracted: ${Object.keys(res.data.kpis_extracted || {}).length}`);
    } catch (e) { alert(e.response?.data?.detail || 'Upload failed.'); }
  };

  const handleStatusChange = async (campaignId, status) => {
    try {
      await axios.patch(`${API}/email-marketing/campaigns/${campaignId}`, { status }, { headers: authHeaders() });
      await fetchData();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to update.'); }
  };

  const handleDelete = async (campaignId) => {
    if (!window.confirm('Delete this campaign?')) return;
    await axios.delete(`${API}/email-marketing/campaigns/${campaignId}`, { headers: authHeaders() });
    await fetchData();
  };

  return (
    <div className="space-y-6 animate-fadeInUp" data-testid="email-marketing-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit' }}>Email Marketing</h1>
          <p className="text-slate-500 text-lg">Submit campaign requirements, upload mailing lists, and view performance reports.</p>
        </div>
        {firmId && (
          <button onClick={() => setShowNew(true)} className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-lg text-sm font-semibold hover:shadow-lg hover:shadow-violet-500/30" data-testid="new-campaign-btn">
            <Plus className="w-4 h-4" /> New Campaign
          </button>
        )}
      </div>

      {error && <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl p-4 text-sm">{error}</div>}

      {!firmId ? (
        <div className="bg-white/70 rounded-2xl border border-white/50 p-12 text-center text-slate-500 flex items-center justify-center gap-2"><AlertCircle className="w-5 h-5" /> Select a client to view campaigns.</div>
      ) : loading ? (
        <div className="bg-white/70 rounded-2xl border border-white/50 p-10 text-center text-slate-500">Loading…</div>
      ) : !campaigns.length ? (
        <div className="bg-white/70 rounded-2xl border border-white/50 p-12 text-center" data-testid="email-marketing-empty">
          <Mail className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <h3 className="text-lg font-semibold text-slate-700 mb-1">No campaigns yet</h3>
          <p className="text-slate-500">Click "New Campaign" to submit your first email marketing request.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {campaigns.map(c => (
            <div key={c.id} className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 p-5 soft-shadow hover-lift" data-testid={`campaign-card-${c.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-900 truncate">{c.name}</h3>
                  {c.objective && <p className="text-xs text-slate-500 mt-0.5">{c.objective}</p>}
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${STATUS_BADGE[c.status] || STATUS_BADGE.draft}`}>{STATUS_LABEL[c.status] || c.status}</span>
              </div>
              {c.target_audience && <p className="text-sm text-slate-600 mb-2"><span className="font-medium">Audience:</span> {c.target_audience}</p>}
              {c.mailing_list_filename && (
                <p className="text-xs text-emerald-700 mb-2 flex items-center gap-1"><FileText className="w-3 h-3" /> List: {c.mailing_list_filename} ({c.mailing_list_size} contacts)</p>
              )}
              <div className="grid grid-cols-3 gap-2 my-3 text-center">
                <div className="bg-slate-50 rounded-lg p-2">
                  <div className="text-xs text-slate-500">Sent</div>
                  <div className="text-sm font-bold text-slate-800">{(c.sent_count || 0).toLocaleString()}</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-2">
                  <div className="text-xs text-slate-500">Opens</div>
                  <div className="text-sm font-bold text-emerald-700">{(c.open_count || 0).toLocaleString()}</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-2">
                  <div className="text-xs text-slate-500">Clicks</div>
                  <div className="text-sm font-bold text-violet-700">{(c.click_count || 0).toLocaleString()}</div>
                </div>
              </div>
              {c.report_files && c.report_files.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs text-slate-500 mb-1 font-medium">Reports ({c.report_files.length})</p>
                  <div className="space-y-1">
                    {c.report_files.slice(-3).map(rpt => (
                      <a key={rpt.id} href={`${API}/email-marketing/campaigns/${c.id}/report/${rpt.id}/download?token=${localStorage.getItem('token')}`}
                        onClick={async (e) => {
                          e.preventDefault();
                          const res = await fetch(`${API}/email-marketing/campaigns/${c.id}/report/${rpt.id}/download`, { headers: authHeaders() });
                          const blob = await res.blob();
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a'); a.href = url; a.download = rpt.filename; a.click(); URL.revokeObjectURL(url);
                        }}
                        className="text-xs text-violet-600 hover:underline flex items-center gap-1" data-testid={`report-link-${rpt.id}`}>
                        <Download className="w-3 h-3" /> {rpt.filename}
                      </a>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                <label className="cursor-pointer">
                  <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => handleListUpload(c.id, e.target.files?.[0])} data-testid={`upload-list-${c.id}`} />
                  <span className="flex items-center gap-1 px-3 py-1.5 bg-white border border-slate-200 rounded-md text-xs font-medium text-slate-700 hover:bg-slate-50"><Upload className="w-3 h-3" /> Mailing List</span>
                </label>
                {canEdit && (
                  <label className="cursor-pointer">
                    <input type="file" accept=".csv" className="hidden" onChange={(e) => handleReportUpload(c.id, e.target.files?.[0])} data-testid={`upload-report-${c.id}`} />
                    <span className="flex items-center gap-1 px-3 py-1.5 bg-violet-50 border border-violet-200 rounded-md text-xs font-medium text-violet-700 hover:bg-violet-100"><TrendingUp className="w-3 h-3" /> Upload Report CSV</span>
                  </label>
                )}
                {canEdit && (
                  <select value={c.status} onChange={(e) => handleStatusChange(c.id, e.target.value)} className="text-xs px-2 py-1.5 border border-slate-200 rounded-md bg-white" data-testid={`status-${c.id}`}>
                    {Object.entries(STATUS_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                )}
                {canEdit && (
                  <button onClick={() => handleDelete(c.id)} className="ml-auto p-1.5 text-rose-500 hover:bg-rose-50 rounded" data-testid={`delete-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                )}
              </div>
              {c.summary && <p className="mt-3 text-xs text-slate-600 bg-slate-50 rounded p-2 italic">{c.summary}</p>}
            </div>
          ))}
        </div>
      )}

      <NewCampaignModal open={showNew} onClose={() => setShowNew(false)} onCreated={() => fetchData()} firmId={firmId} />
    </div>
  );
}
